import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import StandardInputText from '@/common/components/input/StandardInputText';
import StandardInputTextarea from '@/common/components/input/StandardInputTextarea';
import { AddIcon, CancelIcon, DeleteIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import LocalStorageService from '@/services/storage/general/local-storage-service';
import { CourseData, SubunitBuilderData, UnitBuilderData, UnitData, UnitId } from 'course-types';
import LoadingSpinner from '../../LoadingSpinner';

const DefaultUnit: UnitBuilderData = {
  name: '',
  description: '',
  subunits: [],
};

const DefaultSubunit: SubunitBuilderData = {
  name: '',
  activities: [],
};

function UnitCreatorWizard() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const modalVisible: boolean = useAppSelector(() => store.getState().modal.visible);
  const modalName: string = useAppSelector(() => store.getState().modal.name);
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [unitCount, setUnitCount] = useState<number>(0);
  const [newUnitData, setNewUnitData] = useState<UnitBuilderData>(DefaultUnit);
  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState<UnitId | null>(null);

  useEffect(() => {
    setUnitCount(Object.entries(activeCourseData.units ?? {}).length);
  }, [activeCourseData]);

  const onRemoveSubunit = (index: number) => {
    setNewUnitData({
      ...newUnitData,
      subunits: [...newUnitData.subunits.slice(0, index), ...newUnitData.subunits.slice(index + 1)],
    });
  };

  const onSubunitChange = (index: number, name: string) => {
    setNewUnitData({
      ...newUnitData,
      subunits: [
        ...newUnitData.subunits.slice(0, index),
        { ...newUnitData.subunits[index], name: name },
        ...newUnitData.subunits.slice(index + 1),
      ],
    });
  };

  const readyToSubmit = (): boolean => {
    return !(
      newUnitData.name == '' ||
      newUnitData.subunits.length < 1 ||
      newUnitData.subunits.some((sub) => sub.name == '')
    );
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await addNewUnit(newUnitData, unitCount);
      handleClose();
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewUnitData(DefaultUnit);
    setConfirmCloseModalOpen(false);
    setIsLoading(false);
    dispatch(setModalVisible(false));
    dispatch(setModalName(''));
  };

  const handleConfirmClose = () => {
    if (newUnitData.name.trim() !== '' || newUnitData.subunits.length > 0) {
      setConfirmCloseModalOpen(true);
    } else {
      handleClose();
    }
  };

  const addNewUnit = async (unitData: UnitBuilderData, index: number) => {
    try {
      const response = await (
        await Request.post('course/createUnit', {
          unit: unitData,
          index: index,
          courseId: activeCourseData.id,
        })
      )?.json();

      if (!response || response.error || response.status != 'success') {
        throw Error('Failed to add new Unit');
      } else {
        toast.success('Unit added successfully');
        const createdUnit: UnitData & { id: string } = response.data;

        const normalizedSubunits = Object.fromEntries(
          Object.entries(createdUnit.subunits ?? {}).map(([sid, sub]) => [
            sid,
            {
              name: sub.name,
              index: sub.index ?? 0,
              activities: Array.isArray(sub.activities) ? sub.activities : [],
              aurora_activities: Array.isArray((sub as any).aurora_activities)
                ? (sub as any).aurora_activities
                : [],
            },
          ]),
        );

        const normalizedUnit: UnitData = {
          name: createdUnit.name,
          index: createdUnit.index,
          description: createdUnit.description ?? '',
          subunits: normalizedSubunits as UnitData['subunits'],
          activities: Array.isArray((createdUnit as any).activities)
            ? (createdUnit as any).activities
            : [],
        };

        const updatedCourse: CourseData = {
          ...activeCourseData,
          units: {
            ...activeCourseData.units,
            [createdUnit.id]: normalizedUnit,
          },
        };
        dispatch(setActiveCourseData(updatedCourse));

        const cookieCourse = UserCourseStorageService.getCourseData();
        if (cookieCourse && cookieCourse.id === activeCourseData.id) {
          const updatedCookieCourse: CourseData = {
            ...cookieCourse,
            units: {
              ...cookieCourse.units,
              [createdUnit.id]: normalizedUnit,
            },
          };
          LocalStorageService.set('course-map', updatedCookieCourse);
        }
      }
    } catch (error) {
      toast.error((error as Error).message);
      throw error;
    }
  };

  return (
    <>
      <ModalComponent
        isOpen={modalVisible && modalName == 'AddNewUnit'}
        onRequestClose={handleConfirmClose}
        contentLabel='Create New Unit'
        contentSubtitle={`Creating unit ${unitCount + 1}`}
        className='max-w-[800px] w-full'
        closeOnClickOff={false}
      >
        <div className='relative w-full'>
          <div className='flex flex-col gap-2 w-full'>
            <div className='flex flex-col gap-1 bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
              <p className='text-base'>Add a new unit to your course.</p>
              <p className='text-sm text-text_gray_900'>
                Units help organize your course content. Each unit should contain at least one
                subunit.
              </p>
            </div>

            <div className='bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
              <div className='flex flex-col gap-3'>
                <StandardInputText
                  id='unitName'
                  label='Unit Name'
                  isRequired={true}
                  value={newUnitData.name}
                  onChange={(e) => setNewUnitData({ ...newUnitData, name: e.target.value })}
                  disabled={isLoading}
                />

                <StandardInputTextarea
                  id='unitDescription'
                  label='Unit Description'
                  value={newUnitData.description ?? ''}
                  onChange={(e) => setNewUnitData({ ...newUnitData, description: e.target.value })}
                  rows={3}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className='bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
              <div className='flex justify-between items-center mb-3'>
                <p className='text-base font-semibold'>Subunits</p>
                <Button
                  text={true}
                  onClick={() =>
                    setNewUnitData({
                      ...newUnitData,
                      subunits: [...newUnitData.subunits, DefaultSubunit],
                    })
                  }
                  disabled={isLoading}
                >
                  <AddIcon className='w-[24px] h-[24px] mr-1' /> Add Subunit
                </Button>
              </div>

              <div className='flex flex-col gap-2 max-h-[20vh] min-h-[100px] overflow-y-auto pr-2'>
                {newUnitData.subunits.map((subunit, index) => (
                  <div
                    key={`subunit-${index}`}
                    className='bg-surface_gray_50 rounded-lg p-3 border border-border_gray_300'
                  >
                    <div className='flex justify-between items-center mb-2'>
                      <p className='text-base font-semibold'>Subunit {index + 1}</p>
                      <Button
                        text={true}
                        onClick={() => onRemoveSubunit(index)}
                        disabled={isLoading}
                        delete
                      >
                        <DeleteIcon className='w-[20px] h-[20px]' />
                      </Button>
                    </div>
                    <StandardInputText
                      id={String(index)}
                      label='Subunit Name'
                      isRequired={true}
                      value={subunit.name}
                      onChange={(e) => onSubunitChange(index, e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className='flex flex-row gap-8 py-2'>
              <Button onClick={handleSubmit} disabled={!readyToSubmit() || isLoading}>
                <AddIcon className='w-[20px] h-[20px] mr-2' /> Create Unit
              </Button>
              <Button outline onClick={handleConfirmClose} disabled={isLoading}>
                <CancelIcon className='w-[20px] h-[20px] mr-2' /> Cancel
              </Button>
            </div>
          </div>

          {isLoading && (
            <div className='absolute top-0 left-0 z-10 w-full h-full flex items-center justify-center'>
              <div className='absolute inset-0 bg-surface_gray_50 opacity-70' />
              <div className='flex flex-col items-center justify-center gap-2 text-sm z-20'>
                <LoadingSpinner />
                <div>Creating unit...</div>
              </div>
            </div>
          )}
        </div>
      </ModalComponent>

      <ModalComponent
        isOpen={confirmCloseModalOpen}
        onRequestClose={() => setConfirmCloseModalOpen(false)}
        contentLabel='Discard Changes?'
        contentSubtitle='Your changes will be lost'
        className='max-w-[500px] w-full'
        closeOnClickOff={true}
      >
        <div className='relative w-full'>
          <div className='flex flex-col gap-2 w-full'>
            <div className='flex flex-col gap-1 bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
              <p className='text-base'>Are you sure you want to discard your changes?</p>
              <p className='text-sm text-text_gray_900'>
                The unit you&apos;re creating will not be saved.
              </p>
            </div>

            <div className='flex flex-row gap-8 py-2'>
              <Button onClick={handleClose} delete>
                Discard Changes
              </Button>
              <Button outline onClick={() => setConfirmCloseModalOpen(false)}>
                Continue Editing
              </Button>
            </div>
          </div>
        </div>
      </ModalComponent>
    </>
  );
}

export default UnitCreatorWizard;
